"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { HiOutlineMail } from "react-icons/hi";
import { EmailMessageItem } from "@/app/[tenant]/(console)/comms/chat/window/message/email-thread-dialog/email-message-item";
import type { ResumeDraftInfo } from "@/app/[tenant]/(console)/comms/chat/window/message/email-thread-dialog/email-reply-composer";
import { EmailThreadSkeleton } from "@/components/email-composer/email-skeleton";
import type { ComposerDraftFlush } from "@/components/email-composer/hooks/use-email-draft-autosave";
import { useTimezone } from "@/hooks/use-timezone";
import { cx } from "@/lib/tailwind-utils";
import { toast } from "@/lib/use-toast";
import type { EmailMessage, EmailThread, EmailThreadScope } from "@/types/email";
import { EmailDirection, EmailParticipantRole } from "@/types/enums/email";

// Stable empty set so "no fresh reply open" doesn't churn render identity.
const NO_DRAFT_IDS: ReadonlySet<string> = new Set();
function draftRecipientEmails(message: EmailMessage, role: EmailParticipantRole): string[] {
  return message.participants.filter(p => p.role === role).map(p => p.contact.email).filter((email): email is string => !!email);
}
interface EmailThreadViewProps {
  thread: EmailThread | null | undefined;
  isLoading?: boolean;
  selectedMessageId?: string;
  chatId?: string;
  showHeader?: boolean;
  enableMessageActions?: boolean;
  expandLatestMessage?: boolean;
  autoScrollExpandedMessage?: boolean;
  threadScope?: EmailThreadScope;
  resetKey?: string | number | null;
  className?: string;
  bodyClassName?: string;
}
function formatLastUpdated(dateString: string | null, timeZone: string): string {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone
  });
}
export function EmailThreadView({
  thread,
  isLoading = false,
  selectedMessageId,
  chatId,
  showHeader = true,
  enableMessageActions = true,
  expandLatestMessage = false,
  autoScrollExpandedMessage = true,
  threadScope = "provider",
  resetKey,
  className,
  bodyClassName
}: EmailThreadViewProps) {
  const timeZone = useTimezone();
  const [replyingToMessageId, setReplyingToMessageId] = useState<string | null>(null);
  const [forwardingMessageId, setForwardingMessageId] = useState<string | null>(null);
  const [sentReplyKey, setSentReplyKey] = useState<string | null>(null);
  // The draft ids that already existed when the current fresh reply/forward
  // opened. A fresh reply is anchored under a real message and spawns a *new*
  // draft; this lets us tell that new draft apart so we can hide its standalone
  // card (it's already shown as the open composer). Empty unless a fresh
  // reply/forward is open.
  const [draftIdsBeforeReply, setDraftIdsBeforeReply] = useState<ReadonlySet<string>>(NO_DRAFT_IDS);
  const handleReplySent = useCallback(() => {
    setSentReplyKey(`reply-${Date.now()}`);
  }, []);

  // The open reply/forward composer registers its draft flush here while mounted. Its
  // own unmount flush is fire-and-forget — once React tears the editor down there's
  // nothing left to retry with — so every teardown this view controls (switching to
  // another message's composer, closing one) flushes through here first and aborts the
  // switch if the save fails, leaving the composer and its unsaved reply on screen.
  const composerFlushRef = useRef<ComposerDraftFlush | null>(null);
  const registerComposerFlush = useCallback((flush: ComposerDraftFlush | null) => {
    composerFlushRef.current = flush;
  }, []);
  const flushOpenComposer = useCallback(async (): Promise<boolean> => {
    const flush = composerFlushRef.current;
    if (!flush) return true;
    try {
      await flush();
      return true;
    } catch {
      toast({
        title: "Couldn't save draft — your message is still here.",
        variant: "error"
      });
      return false;
    }
  }, []);

  // On open, resume an in-progress draft straight into its composer (matching how
  // it was authored — a forward draft reopens as a forward) instead of showing the
  // collapsed "Edit draft" card. Opens the newest draft when several exist. Keyed
  // on the opened thread (resetKey / thread id) so it fires once per open and never
  // on a background refetch — autosave invalidates the thread, and re-running here
  // would yank an in-progress composer onto a different message. Read-only views
  // (enableMessageActions=false) can't edit, so they stay collapsed.
  useEffect(() => {
    const drafts = enableMessageActions ? thread?.messages.filter(m => m.isDraft) ?? [] : [];
    // Messages are ordered oldest→newest, so the last draft is the most recent.
    const draft = drafts.at(-1) ?? null;
    setReplyingToMessageId(draft && draft.draftKind !== "FORWARD" ? draft.id : null);
    setForwardingMessageId(draft && draft.draftKind === "FORWARD" ? draft.id : null);
    setSentReplyKey(null);
    setDraftIdsBeforeReply(NO_DRAFT_IDS);
    // Re-run only when the opened thread changes, not on every thread refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey, thread?.id, enableMessageActions]);

  // Each draft message resumes from itself; a reply/forward to a real message
  // starts fresh (no resume). Keyed by message id so identities stay stable across
  // re-renders — only a thread refetch rebuilds the map.
  const resumeDraftByMessageId = useMemo(() => {
    const messages = thread?.messages ?? [];
    const map = new Map<string, ResumeDraftInfo>();
    for (const m of messages) {
      if (m.isDraft) {
        map.set(m.id, {
          draftId: m.id,
          bodyHtml: m.bodyHtml ?? "",
          to: draftRecipientEmails(m, EmailParticipantRole.TO),
          cc: draftRecipientEmails(m, EmailParticipantRole.CC),
          bcc: draftRecipientEmails(m, EmailParticipantRole.BCC),
          parentMessage: messages.find(parent => parent.id === m.parentMessageId)
        });
      }
    }
    return map;
  }, [thread?.messages]);

  // Snapshot the existing drafts when opening a fresh reply/forward (anchored to a
  // real message) so the new draft it creates can be told apart and suppressed.
  // Editing an existing draft anchors to the draft itself — nothing to suppress.
  const snapshotDraftsForFreshReply = (anchorId: string | null) => {
    const anchor = anchorId ? thread?.messages.find(m => m.id === anchorId) : undefined;
    if (anchor && !anchor.isDraft) {
      setDraftIdsBeforeReply(new Set((thread?.messages ?? []).filter(m => m.isDraft).map(m => m.id)));
    } else {
      setDraftIdsBeforeReply(NO_DRAFT_IDS);
    }
  };

  // Both handlers also run on send/cancel, where the composer has already discarded its
  // draft — the flush is a no-op there, so it's safe to gate every path on it.
  const handleReply = async (id: string | null) => {
    if (!(await flushOpenComposer())) return;
    setReplyingToMessageId(id);
    if (id) setForwardingMessageId(null);
    snapshotDraftsForFreshReply(id);
  };
  const handleForward = async (id: string | null) => {
    if (!(await flushOpenComposer())) return;
    setForwardingMessageId(id);
    if (id) setReplyingToMessageId(null);
    snapshotDraftsForFreshReply(id);
  };

  // A fresh reply/forward is anchored under a real message; suppress the standalone
  // card for the new draft it creates so it isn't shown twice (once as the open
  // composer, once as a collapsed row).
  const anchorId = replyingToMessageId ?? forwardingMessageId;
  const anchorMessage = anchorId ? thread?.messages.find(m => m.id === anchorId) ?? null : null;
  const suppressNewDraftCards = !!anchorMessage && !anchorMessage.isDraft;
  const messageCount = thread?.messages?.length ?? 0;
  const lastMessageDate = thread?.lastMessageDate ?? null;
  return <div className={cx("flex min-h-0 flex-1 flex-col", className)}>
      {showHeader && <header className="shrink-0 border-b border-border px-6 py-5">
          <div className="flex items-start gap-3">
            <div className="shrink-0 rounded-lg bg-background-secondary p-2">
              <HiOutlineMail className="h-5 w-5 text-foreground-secondary" />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="line-clamp-2 text-h2">
                {isLoading ? <span className="inline-block h-6 w-3/4 animate-pulse rounded bg-muted" /> : thread?.subject || "No Subject"}
              </h2>
              <p className="mt-1 text-body text-foreground-muted">
                {isLoading ? <span className="inline-block h-4 w-1/3 animate-pulse rounded bg-muted" /> : <>
                    {messageCount} {messageCount === 1 ? "message" : "messages"}
                    {lastMessageDate && <>
                        {" "}
                        &middot; Last updated{" "}
                        {formatLastUpdated(lastMessageDate, timeZone)}
                      </>}
                  </>}
              </p>
            </div>
          </div>
        </header>}

      <div className={cx("min-h-0 flex-1 overflow-y-auto py-5 md:px-0", bodyClassName)}>
        {isLoading ? <EmailThreadSkeleton /> : thread?.messages && thread.messages.length > 0 ? <div className="flex flex-col divide-y divide-border/60">
            {thread.messages.map((message, index) => {
          // Hide the standalone card for the draft a fresh reply/forward is
          // actively creating — it's already shown as the open composer.
          if (suppressNewDraftCards && message.isDraft && !draftIdsBeforeReply.has(message.id)) {
            return null;
          }
          const isLastOutbound = index === thread.messages.length - 1 && message.direction === EmailDirection.OUTBOUND;
          const isSentReply = !!(sentReplyKey && isLastOutbound);
          const isLatestMessage = index === thread.messages.length - 1;
          return <EmailMessageItem key={isSentReply ? sentReplyKey : message.id} message={message} defaultExpanded={message.id === selectedMessageId || isSentReply || expandLatestMessage && isLatestMessage} threadId={thread.id} chatId={chatId} threadScope={threadScope} showActions={enableMessageActions} autoScrollOnExpand={autoScrollExpandedMessage} replyingToMessageId={replyingToMessageId} onReply={handleReply} onReplySent={handleReplySent} forwardingMessageId={forwardingMessageId} onForward={handleForward} registerComposerFlush={registerComposerFlush} resumeDraft={resumeDraftByMessageId.get(message.id)} />;
        })}
          </div> : <div className="py-8 text-center text-foreground-muted">
            No messages in this thread
          </div>}
      </div>
    </div>;
}